// Import React tools
import { ArrowDownCollapseIcon, ArrowUpCollapseIcon } from "global/strich_icons/StrichIcons";
import React, { useMemo, useState } from "react";

// This rounds break-even sales up to a whole number
const getBreakEvenSales = (subscriptionCostDifference, savingsPerSale) => {

    // If there is no subscription increase or no saving return null
    if (subscriptionCostDifference <= 0 || savingsPerSale <= 0) {
        return null;
    }

    // Return rounded-up break-even quantity
    return Math.ceil(subscriptionCostDifference / savingsPerSale);

};

// This renders one product pricing card
export default function ProductPricingCard({
    product,
    plans,
    currentSubscriptionLevel,
    currentPlan,
    comparisonLevels = [],
    showIncludingTax,
    selectedForBreakEven,
    onSelectForBreakEven,
    isOpen,
    onToggleOpen,
    formatMoney,
    findPriceRowByLevel,
    getRateVisiblePrice,
    getProductTotalAtLevel,
    getPackageVisiblePrice,
    getMonthlyEquivalent,
}) {

    // Store selected print areas for this card
    const [selectedPrintAreas, setSelectedPrintAreas] = useState(() => (product?.print_areas || []).map((printArea) => printArea?.role));

    // Calculate display rows for all print areas selected count
    const selectedPrintAreaCount = selectedPrintAreas.length;

    // Check whether any print areas are selected
    const hasSelectedPrintAreas = selectedPrintAreaCount > 0;

    // This gets the monthly subscription cost for a plan using the requested tax mode
    const getPlanMonthlyCost = (plan, includingTax = showIncludingTax) => {

        // Free plans have no monthly cost
        if (plan?.packages?.free) {
            return 0;
        }

        // Prefer the actual monthly package when present
        if (plan?.packages?.monthly) {
            return getPackageVisiblePrice(plan.packages.monthly, includingTax);
        }

        // Fall back to the monthly equivalent of yearly billing
        if (plan?.packages?.yearly) {
            return getMonthlyEquivalent(plan.packages.yearly, includingTax);
        }

        // Return zero when there is no package cost
        return 0;

    };

    // This gets product prices for one subscription level using the requested tax mode
    const getLevelPrices = (level, includingTax = showIncludingTax) => {

        // Get the base row
        const baseRow = findPriceRowByLevel(product?.base_price || [], level);

        // Get the base price
        const basePrice = getRateVisiblePrice(baseRow, includingTax);

        // Get total price for the selected print areas
        const totalPrice = getProductTotalAtLevel({
            product,
            level,
            selectedPrintAreas,
            showIncludingTax: includingTax,
        });

        // Get print price as the difference between total and base
        const printPrice = hasSelectedPrintAreas ? totalPrice - basePrice : 0;

        // Return all prices
        return {
            basePrice,
            printPrice,
            totalPrice: hasSelectedPrintAreas ? totalPrice : basePrice,
        };

    };

    // Get current subscription prices for display
    const currentPrices = getLevelPrices(currentSubscriptionLevel);

    // Get canonical exc-tax current prices for break-even calculations
    const currentPricesExcTax = getLevelPrices(currentSubscriptionLevel, false);

    // Get the canonical exc-tax current subscription cost for break-even calculations
    const currentPlanMonthlyCostExcTax = getPlanMonthlyCost(currentPlan, false);

    // Build visible comparison plans
    const visibleComparisonPlans = useMemo(() => {

        // Store selected level numbers
        const selectedLevelNumbers = new Set((comparisonLevels || []).map((level) => Number(level)));

        // Always include the current subscription level
        selectedLevelNumbers.add(Number(currentSubscriptionLevel));

        // Return matching plans in plan order
        return (plans || []).filter((plan) => selectedLevelNumbers.has(Number(plan?.level)));

    }, [plans, comparisonLevels, currentSubscriptionLevel]);

    // This formats difference from the current plan price
    const formatDifference = (difference) => {

        // Current plan difference is blank
        if (Math.abs(Number(difference || 0)) < 0.005) {
            return "—";
        }

        // Prefix positive values clearly
        if (difference > 0) {
            return "+" + formatMoney(difference);
        }

        // Negative values are formatted by the currency formatter
        return formatMoney(difference);

    };

    // This gets the difference class for a comparison row
    const getDifferenceClassName = (difference, isCurrentLevel) => {

        // Current level row gets its own class
        if (isCurrentLevel) {
            return "pod-pricing-page__comparison-row--current";
        }

        // Lower price than current is a saving
        if (difference < 0) {
            return "pod-pricing-page__comparison-row--saving";
        }

        // Higher price than current is an increase
        if (difference > 0) {
            return "pod-pricing-page__comparison-row--increase";
        }

        // No change
        return "";

    };

    // This renders one comparison section table
    const renderComparisonSection = ({ title, priceKey, currentPrice, showBreakEven = false }) => {

        // Return one comparison section
        return (

            <div className="pod-pricing-page__comparison-section" key={priceKey}>

                <h4 className="pod-pricing-page__comparison-section-title">{title}</h4>

                <div className="pod-pricing-page__table-scroll">

                    <table className="pod-pricing-page__detail-table pod-pricing-page__comparison-table">

                        <thead>
                            <tr>
                                <th>Plan</th>
                                <th>Price</th>
                                <th>Difference</th>
                                {showBreakEven && <th>Break Even</th>}
                            </tr>
                        </thead>

                        <tbody>

                            {visibleComparisonPlans.map((plan) => {

                                // Get prices for this level
                                const levelPrices = getLevelPrices(plan?.level);

                                // Get this section's price
                                const rowPrice = levelPrices[priceKey];

                                // Calculate difference from current subscription
                                const differenceFromCurrent = rowPrice - currentPrice;

                                // Check if this is the current subscription level
                                const isCurrentLevel = Number(plan?.level) === Number(currentSubscriptionLevel);

                                // Get canonical exc-tax prices for this plan's break-even calculation
                                const levelPricesExcTax = getLevelPrices(plan?.level, false);

                                // Get this plan's canonical exc-tax monthly subscription cost
                                const planMonthlyCostExcTax = getPlanMonthlyCost(plan, false);

                                // Calculate the exc-tax subscription cost increase versus the current plan
                                const subscriptionCostDifference = planMonthlyCostExcTax - currentPlanMonthlyCostExcTax;

                                // Calculate the canonical exc-tax saving per sale for this section
                                const savingsPerSale = currentPricesExcTax[priceKey] - levelPricesExcTax[priceKey];

                                // Calculate break-even quantity independently of the display tax mode
                                const breakEvenQuantity = showBreakEven ? getBreakEvenSales(subscriptionCostDifference, savingsPerSale) : null;

                                // Return comparison row
                                return (

                                    <tr
                                        key={`${product?.sku}-${priceKey}-${plan?.level}`}
                                        className={getDifferenceClassName(differenceFromCurrent, isCurrentLevel)}
                                    >

                                        <td>{plan?.name}</td>

                                        <td>{formatMoney(rowPrice)}</td>

                                        <td>{formatDifference(differenceFromCurrent)}</td>

                                        {showBreakEven && <td>{breakEvenQuantity ? `${breakEvenQuantity} sales` : "—"}</td>}

                                    </tr>

                                );

                            })}

                        </tbody>

                    </table>

                </div>

            </div>

        );

    };

    // This toggles a print area on or off
    const handleTogglePrintArea = (printAreaName) => {
        setSelectedPrintAreas((prev) => {
            const alreadySelected = prev.includes(printAreaName);

            // Keep at least one print area selected
            if (alreadySelected && prev.length === 1) {
                return prev;
            }

            if (alreadySelected) {
                return prev.filter((item) => item !== printAreaName);
            }

            return [...prev, printAreaName];
        });
    };

    // Render product card
    return (
        
        <article
            className={`
                pod-pricing-page__product-card
                ${selectedForBreakEven ? "pod-pricing-page__product-card--focused" : ""}
                ${isOpen ? "pod-pricing-page__product-card--open" : ""}
            `}
            onClick={(e) => {

                e.stopPropagation()

                onSelectForBreakEven({
                    ...product,
                    selectedPrintAreas,
                });

                onToggleOpen(product?.sku);
            }}
        >

            {/* This is the product header */}
            <div className="pod-pricing-page__product-header">

                {/* This is the product info area */}
                <div className="pod-pricing-page__product-info">

                    {/* This is the product title and expand indicator */}
                    <div className="pod-pricing-page__product-title-row">

                        <span className="pod-pricing-page__product-expand-icon" title={isOpen ? "Collapse product pricing" : "Expand product pricing"}>
                            {isOpen ? <ArrowUpCollapseIcon /> : <ArrowDownCollapseIcon />}
                        </span>

                        <h3 className="pod-pricing-page__product-title">{product?.title}</h3>

                    </div>

                    {/* This is the product sku subtitle */}
                    <p className="pod-pricing-page__product-sku">{product?.sku}</p>

                    {/* This is the print area chooser */}
                    <div className="pod-pricing-page__print-area-toggle-group">

                        {(product?.print_areas || []).map((printArea) => {

                            const isSelected = selectedPrintAreas.includes(printArea?.role);

                            return (
                                
                                <button
                                    key={`${product?.sku}-${printArea?.role}`}
                                    type="button"
                                    className={`pod-pricing-page__chip-button ${isSelected ? "pod-pricing-page__chip-button--active" : ""}`}
                                    onClick={(event) => {
                                        event.stopPropagation();
                                        handleTogglePrintArea(printArea?.role);
                                    }}
                                >

                                    {printArea?.name}

                                </button>

                            );

                        })}

                    </div>

                </div>

                {/* This is the highlighted summary area */}
                <div className="pod-pricing-page__product-summary-wrap">
                    <div className="pod-pricing-page__product-summary-grid">

                        <div className="pod-pricing-page__summary-card">
                            <span className="pod-pricing-page__summary-label">Base Cost</span>
                            <strong className="pod-pricing-page__summary-value">{formatMoney(currentPrices.basePrice)}</strong>
                        </div>

                        <div className="pod-pricing-page__summary-card">
                            <span className="pod-pricing-page__summary-label">Print Areas</span>
                            <strong className="pod-pricing-page__summary-value">{selectedPrintAreaCount}</strong>
                        </div>

                        <div className="pod-pricing-page__summary-card">
                            <span className="pod-pricing-page__summary-label">Print Price</span>
                            <strong className="pod-pricing-page__summary-value">{hasSelectedPrintAreas ? formatMoney(currentPrices.printPrice) : "-"}</strong>
                        </div>

                        <div className="pod-pricing-page__summary-card pod-pricing-page__summary-card--estimated">
                            <span className="pod-pricing-page__summary-label">Total</span>
                            <strong className="pod-pricing-page__summary-value">{formatMoney(currentPrices.totalPrice)}</strong>
                        </div>

                    </div>

                </div>

            </div>

            {/* This is the expandable detail area */}
            {isOpen && (

                <div className="pod-pricing-page__product-details">

                    <div className="pod-pricing-page__comparison-layout">

                        {renderComparisonSection({
                            title: "Base",
                            priceKey: "basePrice",
                            currentPrice: currentPrices.basePrice,
                        })}

                        {renderComparisonSection({
                            title: "Print",
                            priceKey: "printPrice",
                            currentPrice: currentPrices.printPrice,
                        })}

                        {renderComparisonSection({
                            title: "Total",
                            priceKey: "totalPrice",
                            currentPrice: currentPrices.totalPrice,
                            showBreakEven: true,
                        })}

                    </div>

                </div>
            )}

        </article>
    );

}